import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  RequestToolbar,
  type RequestToolbarProps,
} from '../components/RequestToolbar';

type RequestToolbarFrameProps = RequestToolbarProps | null;

interface RequestFrameContextValue {
  toolbarProps: RequestToolbarFrameProps;
  setToolbarProps: (toolbar: RequestToolbarFrameProps) => void;
}

const RequestFrameContext = createContext<RequestFrameContextValue>({
  toolbarProps: null,
  setToolbarProps: () => undefined,
});

export const RequestFrameProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toolbarProps, setToolbarProps] =
    useState<RequestToolbarFrameProps>(null);

  const value = useMemo(
    () => ({
      toolbarProps,
      setToolbarProps,
    }),
    [toolbarProps],
  );

  return (
    <RequestFrameContext.Provider value={value}>
      {children}
    </RequestFrameContext.Provider>
  );
};

export const RequestFrameToolbarSlot: React.FC = () => {
  const { toolbarProps } = useContext(RequestFrameContext);

  return toolbarProps ? <RequestToolbar {...toolbarProps} /> : null;
};

/** Mount request toolbar props into the frame slot below the global header. */
export function useRequestFrameToolbar(props: RequestToolbarFrameProps) {
  const { setToolbarProps } = useContext(RequestFrameContext);

  const title = props?.title;
  const requestNumber = props?.requestNumber;
  const stage = props?.stage;
  const rawStage = props?.rawStage;
  const isStageUpdating = props?.isStageUpdating;
  const showInternalDetail = props?.showInternalDetail;
  const disabled = props?.disabled;
  const testID = props?.testID;
  const formData = props?.formData;
  const formDataRef = props?.formDataRef;
  const titleAccessory = props?.titleAccessory;
  const actionLeading = props?.actionLeading;
  const actionTrailing = props?.actionTrailing;

  useEffect(() => {
    setToolbarProps(props);

    return () => setToolbarProps(null);
  }, [
    setToolbarProps,
    title,
    requestNumber,
    stage,
    rawStage,
    isStageUpdating,
    showInternalDetail,
    disabled,
    testID,
    formData,
    formDataRef,
    titleAccessory,
    actionLeading,
    actionTrailing,
  ]);
}
